import { useState } from "react";
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Application } from "types/application";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { HugeiconsIcon } from "@hugeicons/react";
import { Loading03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useMakeDecisionController } from "shared/app/MakeDecisionModal/controller";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { RunReportConfirmModal } from "./RunReportConfirmModal";

interface CreditReportBlockedViewProps {
  applicantId: number;
  applicationId: number;
  verificationId: number;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Application | undefined, unknown>>;
}

export const CreditReportBlockedView = ({
  applicantId,
  applicationId,
  verificationId,
  refetch
}: CreditReportBlockedViewProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { applicationMeta } = useMakeDecisionController(String(applicationId));

  const { mutate, isPending } = useMutation({
    mutationKey: ["VERIFICATION", applicationId, verificationId, "RUN_REPORT"],
    mutationFn: () => {
      return instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/verifications/${verificationId}/run_report`
      );
    },
    onSuccess: () => {
      refetch();
      applicationMeta.refetch();
      setShowConfirmModal(false);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <>
      <div className="mx-auto flex w-[432px] flex-col items-center gap-6 pt-16">
        <div className="rounded-full bg-warning-50 p-5">
          <HugeiconsIcon
            icon={Loading03Icon}
            size={24}
            className="text-warning-600"
          />
        </div>

        <div className="w-full text-center">
          <p className="text-sm font-semibold text-content-500">
            Waiting for credit report
          </p>
          <p className="mt-2 text-sm font-medium leading-5 text-content-300">
            This report is waiting for the credit report to be completed.
            <br />
            The credit report has an error and cannot be completed.
          </p>
        </div>

        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          onClick={() => setShowConfirmModal(true)}
          loading={isPending}
        >
          Override and run
        </Button>
      </div>

      <RunReportConfirmModal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => mutate()}
        isLoading={isPending}
      />
    </>
  );
};
